import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare } from '@fortawesome/free-regular-svg-icons';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import {
  inspectionReportService,
  type InspectionReport,
  type UpdateReportMetadata,
} from '@/services/inspection-report.service';
import {
  WARRANTY_STATUS_LABELS,
  type InspectionWarrantyStatus,
} from '@/services/inspection.service';

interface Props {
  inspectionId: string;
  report: InspectionReport;
  canEdit: boolean;
  /** The inspection's current warranty coverage; seeds the dropdown. */
  warrantyStatus?: InspectionWarrantyStatus;
}

/**
 * The four coverage states, in resolution order rather than alphabetical: the
 * unresolved default first, then the answer that matters most.
 *
 * Labels come from the shared WARRANTY_STATUS_LABELS the rest of the
 * application already displays, so this dropdown can never drift from the
 * inspection header, the list page or the customer portal.
 */
const WARRANTY_OPTIONS: InspectionWarrantyStatus[] = [
  'PENDING_VERIFICATION',
  'UNDER_WARRANTY',
  'OUT_OF_WARRANTY',
  'NO_WARRANTY',
];

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as
      | {
          error?: { message?: string };
          message?: string | string[];
        }
      | undefined;
    if (body?.error?.message) return body.error.message;
    if (Array.isArray(body?.message)) return body!.message.join(', ');
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

const NARRATIVE_FIELDS: { key: keyof UpdateReportMetadata; label: string }[] = [
  { key: 'asFoundCondition', label: 'As Found Condition' },
  { key: 'finalActionActivities', label: 'Final Action / Activities' },
  { key: 'causeOfFailure', label: 'Cause of Failure' },
  { key: 'finalActionNeeded', label: 'Final Action Need to Take' },
  { key: 'finalActivity', label: 'Final Activity' },
];

/** The classifications an engineer may set on an individual report. */
const CLASSIFICATION_FIELDS: { key: keyof UpdateReportMetadata; label: string }[] = [
  { key: 'clsProductQuality', label: 'Product Quality' },
  { key: 'clsApplicationEngineering', label: 'Application Engineering' },
  { key: 'clsProductAbuse', label: 'Product Abuse' },
];

export function ReportMetadataCard({ inspectionId, report, canEdit, warrantyStatus }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateReportMetadata>({});

  const startEdit = () => {
    setForm({
      asFoundCondition: report.asFoundCondition ?? '',
      finalActionActivities: report.finalActionActivities ?? '',
      causeOfFailure: report.causeOfFailure ?? '',
      finalActionNeeded: report.finalActionNeeded ?? '',
      finalActivity: report.finalActivity ?? '',
      clsProductQuality: report.clsProductQuality,
      clsApplicationEngineering: report.clsApplicationEngineering,
      clsProductAbuse: report.clsProductAbuse,
      // Seeded from the inspection so the dropdown opens on the CURRENT
      // coverage, not on a default. Omitted when unknown, which the backend
      // reads as "leave unchanged" rather than "reset to pending".
      ...(warrantyStatus ? { warrantyStatus } : {}),
    });
    setEditing(true);
  };

  const mutation = useMutation({
    mutationFn: (dto: UpdateReportMetadata) =>
      inspectionReportService.updateMetadata(inspectionId, dto),
    onSuccess: () => {
      toast.success('Report details saved');
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['report', inspectionId] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to save report details')),
  });

  if (!editing) {
    const filledCount = NARRATIVE_FIELDS.filter(({ key }) =>
      ((report[key as keyof InspectionReport] as string | null) ?? '').trim(),
    ).length;

    return (
      <div className="space-y-3" data-testid="report-metadata-view">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Report Details</h3>
            <p className="text-xs text-muted-foreground">
              {filledCount} of {NARRATIVE_FIELDS.length} narrative fields completed
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={startEdit}
              className="shrink-0"
            >
              Edit
            </Button>
          )}
        </div>

        {/*
          One card per narrative, two-up from `sm`. A single flowing list made
          every field look like a continuation of the one above it; a bordered
          block per field gives the eye a boundary to scan by, and an empty field
          is unmistakably empty rather than just short.
        */}
        <dl className="grid gap-2 sm:grid-cols-2">
          {NARRATIVE_FIELDS.map(({ key, label }) => {
            const value = ((report[key as keyof InspectionReport] as string | null) ?? '').trim();
            return (
              <div key={key} className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </dt>
                <dd
                  className={`mt-1 whitespace-pre-wrap break-words text-sm ${
                    value ? 'text-foreground' : 'italic text-muted-foreground'
                  }`}
                  data-empty={value ? undefined : 'true'}
                >
                  {value || 'Not recorded'}
                </dd>
              </div>
            );
          })}
        </dl>

        <div className="rounded-lg border border-border px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Classification
          </p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {CLASSIFICATION_FIELDS.map(({ key, label }) => {
              const on = Boolean(report[key as keyof InspectionReport]);
              return (
                <span
                  key={key}
                  className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs ${
                    on ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={on ? faSquareCheck : faSquare}
                    className="h-3 w-3"
                    aria-hidden="true"
                  />
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-3"
      data-testid="report-metadata-form"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      <h3 className="text-sm font-semibold text-foreground">Edit Report Details</h3>
      {NARRATIVE_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
          <textarea
            value={(form[key] as string) ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            rows={2}
            maxLength={10000}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      ))}
      <div className="flex flex-wrap gap-4">
        {CLASSIFICATION_FIELDS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={Boolean(form[key])}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
              className="h-4 w-4 rounded border-input"
            />
            {label}
          </label>
        ))}
      </div>
      {/*
        Warranty COVERAGE — a different question from the Classification
        checkboxes above, which are unchanged. It saves through this same form
        and the same request; there is no second call and no second endpoint.
      */}
      <div>
        <label
          className="mb-1 block text-xs font-medium text-muted-foreground"
          htmlFor="report-warranty-status"
        >
          Warranty
        </label>
        <select
          id="report-warranty-status"
          value={form.warrantyStatus ?? warrantyStatus ?? 'PENDING_VERIFICATION'}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              warrantyStatus: e.target.value as InspectionWarrantyStatus,
            }))
          }
          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="report-warranty-status"
        >
          {WARRANTY_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {WARRANTY_STATUS_LABELS[status]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          Pending Verification leaves both Yes and No blank on the report.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
